import { Fragment, useState, useEffect } from "react";
import { Container, Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import liff from "../../lib/liff";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import QuestCard from "default/components/QuestCard";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSoulBackend } from "backend";
import Loading from "default/components/Loading";
import ImageWithSaveButton from "default/components/ImageWithSaveButton";
import StyledButton from "default/components/StyledButton";
import AppLayout from "default/components/AppLayout";
import { COLORS } from "constants/colors";
import Error from "default/components/Error";
import TicketNotFound from "default/components/TicketNotFound";
import { useUrlParams } from "hooks/useUrlParams";

// Helper functions
const withLng = (path, lng) => {
  const hasQuery = path.includes("?");
  return lng ? `${path}${hasQuery ? "&" : "?"}lng=${lng}` : path;
};

// Custom hook for quest data management
const useQuests = (soulId, backend) => {
  const [questGroups, setQuestGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [afterPurchase, setAfterPurchase] = useState(true);
  const [isError, setIsError] = useState(false);

  const initQuests = async () => {
    setIsLoading(true);
    setIsError(false);

    try {
      const userEpisodeData = await backend.getEpisode(soulId);

      if (userEpisodeData === null) {
        setAfterPurchase(false);
      } else {
        const questGroupsData = await backend.listQuestGroups(soulId);
        setQuestGroups(questGroupsData);
      }
    } catch (error) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (soulId) {
      initQuests();
    }
  }, [soulId, backend]);

  return {
    questGroups,
    isLoading,
    afterPurchase,
    isError,
  };
};

// Component for empty quests state
const EmptyQuestsView = ({ t }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      minHeight: "100vh",
    }}
  >
    <SearchOffIcon sx={{ fontSize: "100px", color: "text.disabled" }} />
    <Typography
      variant="h6"
      sx={{
        marginTop: "16px",
        marginBottom: "16px",
        color: "text.secondary",
        fontSize: "16px",
      }}
    >
      {t("common:quest.no_quests")}
    </Typography>
  </Box>
);

// Component for quest content
const QuestContent = ({
  questGroups,
  lng,
  t,
  onRequestJudgement,
  onCloseWindow,
}) => (
  <Box
    sx={{
      display: "flex",
      paddingTop: 4,
      paddingBottom: 4,
      flexDirection: "column",
      alignItems: "center",
      gap: 3,
      minHeight: "100vh",
    }}
  >
    {questGroups.map((group, index) => {
      // Helper function to get group title
      const getGroupTitle = (groupName, index) => {
        if (groupName) {
          return groupName;
        }
        // Add more special cases here as needed
        return index === 0 ? "Quest Reward" : "Next Reward";
      };

      return (
        <Fragment key={group.quest_group_key}>
          <Typography
            sx={(theme) => ({
              ...theme.components.pageHeader,
            })}
          >
            {getGroupTitle(group.quest_group_name, index)}
          </Typography>

          <Box sx={{ width: "100%" }}>
            {group.reward_url && (
              <Container
                sx={{
                  display: "flex",
                  paddingLeft: 0,
                  paddingRight: 0,
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                  width: "100%",
                  marginBottom: 3,
                }}
              >
                <ImageWithSaveButton
                  imageUrls={
                    group.quests.every((quest) => quest.is_cleared)
                      ? [group.reward_url]
                      : group.quests
                        .filter(
                          (quest) => quest.is_cleared && quest.reward_url
                        )
                        .map((quest) => quest.reward_url)
                  }
                  showDownloadButton={group.quests.every(
                    (quest) => quest.is_cleared
                  )}
                />
              </Container>
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 2,
              }}
            >
              {group.quests.map((quest) => (
                <QuestCard
                  key={quest.quest_key}
                  quest={quest}
                  style={{ cursor: "pointer" }}
                />
              ))}
            </Box>
          </Box>

          {/* Divider between groups */}
          {index < questGroups.length - 1 && (
            <Box
              sx={(theme) => ({
                width: "100%",
                height: "1px",
                marginTop: 2,
                marginBottom: 2,
                backgroundColor: theme.components.divider.backgroundColor,
              })}
            />
          )}
        </Fragment>
      );
    })}

    {lng === "ja" && (
      <StyledButton
        variant="contained"
        sx={{ marginTop: 4 }}
        onClick={onRequestJudgement}
      >
        {t("common:button.request_clear_judgement")}
      </StyledButton>
    )}

    <StyledButton
      variant="contained"
      sx={{ marginTop: 1 }}
      onClick={onCloseWindow}
    >
      {t("common:button.back_to_chat")}
    </StyledButton>
  </Box>
);

function Quests() {
  const navigate = useNavigate();
  const { soulId, lng, work } = useUrlParams();
  const { t } = useTranslation(["common"]);
  const theme = useTheme();

  const backend = useSoulBackend();
  const { questGroups, isLoading, afterPurchase, isError } = useQuests(
    soulId,
    backend
  );

  const handleNavigateToTickets = () => {
    navigate(withLng(`/${work}/tickets?soul_id=${soulId}`, lng));
  };

  const handleCloseWindow = () => {
    liff.closeWindow();
  };

  const handleRequestJudgement = () => {
    window.open(
      "https://docs.google.com/forms/d/e/1FAIpQLSckxdz-E1hLeEAwtCSYx5zoSNq6s-tDAibm7uEu3xMNDhMI8g/viewform",
      "_blank"
    );
  };

  // Render loading state
  if (isLoading) {
    return (
      <AppLayout page="Quests">
        <Loading />
      </AppLayout>
    );
  }

  // Render error state
  if (isError) {
    return (
      <AppLayout page="Quests">
        <Error />
      </AppLayout>
    );
  }

  // Render no ticket purchased state
  if (!afterPurchase) {
    return (
      <AppLayout page="Quests">
        <TicketNotFound onClick={handleNavigateToTickets} />
      </AppLayout>
    );
  }

  // Render no quests state
  if (questGroups.length === 0) {
    return (
      <AppLayout page="Quests">
        <EmptyQuestsView t={t} />
      </AppLayout>
    );
  }

  // Render main content
  return (
    <AppLayout page="Quests">
      <QuestContent
        questGroups={questGroups}
        lng={lng}
        t={t}
        onRequestJudgement={handleRequestJudgement}
        onCloseWindow={handleCloseWindow}
      />
    </AppLayout>
  );
}

export default Quests;
